import {
  AdditionalPassengerProps,
  Address,
  AirportData,
  FieldValues,
  FileUploadData,
  FlightInfoSelectProblem,
  FlightProblem,
  FlightSearchResult,
  PassengerSignatureProps,
  PassengersInformation,
  TaskData,
} from "@/core/types/types";
import { FileUploadSuccessResponse } from "@/pages/api/uploadFiles";
import Stopover from "@/APR/components/custom/Stopover/Stopover";
import {
  ADDITIONAL_PASSENGER_SIGNATURES_KEY,
  IP_ADDRESS_FIELD,
  USER_AGENT_FIELD,
} from "@/core/data";
import { getTimeZoneProps } from "@/core/utils";

const CONFIRM_PROBLEM_STEP_TITLE: RegExp = /.*confirm-problem.*/;
const DOCUMENT_UPLOAD_STEP_TITLE: RegExp = /^step-\d+-document-upload$/;
const ALTERNATIVE_TRANSPORT_ROUTE_STEP_TITLES: RegExp[] = [
  /^.*alt-transportation-route.*$/,
  /^.*additional-expense-route.*$/,
];
const TRANSPORT_LEG_INPUT_STEP_TITLES: RegExp[] = [
  /.*alt-transportation-itinerary.*/,
  /.*flight-selection.*/,
  /.*additional-expense-itinerary.*/,
];
const FLIGHT_STOPOVERS_STEP_TITLE: RegExp = /.*stopovers.*/;
const MULTIPLE_CHECKBOX_STEP_REGEX: RegExp = /.*multiple-checkbox.*/;
const SIGNATURE_ADDITIONAL_PASSENGERS: RegExp =
  /.*signature-additional-passengers.*/;

interface FormAnswers {
  originIATA: AirportData;
  destinationIATA: AirportData;
  flightSelector?: FlightSearchResult;
  manualDepartureDateTime?: string;
  manualFlightNumber?: string;
  boardingPass?: FileUploadData[];
  flightStopovers: Stopover[];
  altTransportStopovers: Stopover[];
  fieldKey?: string;
  multipleCheckbox?: {
    [checkboxKey: string]: FieldValues;
  };
  extraOptionValue?: string;
  [key: string]: any;
}
export async function beforeSubmit(
  currentStep: TaskData,
  formAnswers: FormAnswers
): Promise<object> {
  const stepTitle = currentStep.extensionProperties.stepTitle;

  if (stepTitleMatches(CONFIRM_PROBLEM_STEP_TITLE, stepTitle)) {
    return beforeProblemSelectStepSubmit(formAnswers);
  }

  if (stepTitleMatches(DOCUMENT_UPLOAD_STEP_TITLE, stepTitle)) {
    return beforeDocumentUploadStepSubmit(formAnswers);
  }

  if (stepTitleMatches(ALTERNATIVE_TRANSPORT_ROUTE_STEP_TITLES, stepTitle)) {
    return beforeAlternativeTransportStepSubmit(formAnswers);
  }

  if (stepTitleMatches(TRANSPORT_LEG_INPUT_STEP_TITLES, stepTitle)) {
    return beforeAlternativeTransportItineraryStepSubmit(formAnswers);
  }

  if (stepTitleMatches(FLIGHT_STOPOVERS_STEP_TITLE, stepTitle)) {
    return beforeFlightStopoversStepSubmit(formAnswers);
  }

  if (stepTitleMatches(MULTIPLE_CHECKBOX_STEP_REGEX, stepTitle)) {
    return beforeMultipleCheckboxStepSubmit(formAnswers);
  }

  if (stepTitleMatches(SIGNATURE_ADDITIONAL_PASSENGERS, stepTitle)) {
    return beforeAdditionalPassengerSignaturesStepSubmit(formAnswers);
  }

  return formAnswers;
}

function stepTitleMatches(expression: RegExp | RegExp[], stepTitle?: string) {
  if (Array.isArray(expression)) {
    return expression.some((exp: RegExp) => exp.test(stepTitle || ""));
  } else {
    return expression.test(stepTitle || "");
  }
}

async function beforeProblemSelectStepSubmit(formAnswers: FormAnswers) {
  const selectedFlightProblems: FlightInfoSelectProblem[] = [];
  Object.keys(formAnswers)
    .filter((key) => key.includes(","))
    .forEach((key, index) => {
      const [originIATA, destinationIATA] = key.split(",");
      const flightProblem: FlightProblem = formAnswers[key];
      const actualArrivalTimeKey: string = "manualActualArrivalDateTime";
      const actualArrivalTime: string =
        formAnswers[actualArrivalTimeKey]?.[index];
      selectedFlightProblems.push({
        originIATA,
        destinationIATA,
        flightProblem,
        actualArrivalTime,
      });
    });

  return {
    selectedFlightProblems,
  };
}

async function beforeDocumentUploadStepSubmit(formAnswers: FormAnswers) {
  const { boardingPass, documentType, documentSubtype } = formAnswers;
  const parsedFiles = boardingPass?.map((file: FileUploadData) => {
    return {
      uuid: file.uuid,
      filename: file.fileName,
      mimeType: file.mimeType,
    } as FileUploadSuccessResponse;
  });

  return {
    boardingPass: parsedFiles,
    documentType,
    documentSubtype,
  };
}

async function beforeAlternativeTransportStepSubmit(formAnswers: FormAnswers) {
  const { altTransportStopovers, ...otherAlternativeTransportData } =
    formAnswers;
  const stopoverData = altTransportStopovers?.map((stopover: Stopover) => {
    const { stopoverAirport, alternativeTransport } = stopover || {};
    const { name, iata } = stopoverAirport || {};
    return {
      name,
      iata,
      mode: alternativeTransport,
    };
  });

  return {
    altTransportStopovers: stopoverData,
    ...otherAlternativeTransportData,
  };
}

async function beforeFlightStopoversStepSubmit(formAnswers: FormAnswers) {
  const { flightStopovers, ...otherAlternativeTransportData } = formAnswers;
  const stopoverData: AirportData[] = flightStopovers
    ?.map((stopover: Stopover): AirportData => {
      const { stopoverAirport }: Stopover = stopover || {};
      const { name, iata }: AirportData = stopoverAirport! || {};
      return {
        name,
        iata,
      };
    })
    .reduce((acc: AirportData[], currentStopover: AirportData) => {
      if (currentStopover?.name && currentStopover?.iata) {
        const { iata, name } = currentStopover;
        return [...acc, { iata, name }];
      } else {
        return [...acc];
      }
    }, []);

  return {
    flightStopovers: stopoverData,
    ...otherAlternativeTransportData,
  };
}

async function beforeAlternativeTransportItineraryStepSubmit(
  formAnswers: FormAnswers
) {
  const fieldKeyName = formAnswers.fieldKey!!;
  return {
    [fieldKeyName]: (formAnswers as any)[fieldKeyName],
  };
}

async function beforeMultipleCheckboxStepSubmit(formAnswers: FormAnswers) {
  // Removes data from the form which is not intended to be stored in camunda
  delete formAnswers?.multipleCheckbox;
  delete formAnswers?.extraOptionValue;
  return formAnswers;
}

async function beforeAdditionalPassengerSignaturesStepSubmit(
  formAnswers: FormAnswers
) {
  const passengerData: AdditionalPassengerProps[] =
    formAnswers[ADDITIONAL_PASSENGER_SIGNATURES_KEY] || [];
  const [leadCustomer, ...additionalPassengersData] = passengerData;
  const { address } = leadCustomer;
  const { timeZone, signedAt } = getTimeZoneProps();
  const signatureProps: PassengerSignatureProps = {
    base64EncodedSignature: "",
    timeZone,
    signedAt,
    ip: formAnswers[IP_ADDRESS_FIELD],
    userAgent: formAnswers[USER_AGENT_FIELD],
  };
  const passengersInformation: PassengersInformation[] =
    additionalPassengersData.map((passenger: AdditionalPassengerProps) => {
      const {
        addressStreet,
        addressPostalCode,
        addressCity,
        addressCountry,
        signatureComponent,
      } = passenger;
      const isPassengerAvailable: boolean =
        passenger.passengerAvailable === "true";
      const isSameAddress: boolean = passenger.sameAddress === "true";
      const currentPassengerAddress: Address = {
        street: addressStreet,
        postalCode: addressPostalCode,
        city: addressCity,
        country: addressCountry,
      };
      const passengerSignature: PassengerSignatureProps | null =
        !isPassengerAvailable
          ? null
          : {
              ...signatureProps,
              base64EncodedSignature: signatureComponent?.signature!,
            };
      const passengerAddress: Address | null = !isPassengerAvailable
        ? null
        : isSameAddress
        ? address
        : currentPassengerAddress;

      return {
        id: passenger.id,
        signature: passengerSignature,
        address: passengerAddress,
      };
    });
  return {
    passengersInformation,
  };
}
